import React from 'react'
import { Link } from 'react-router-dom'

import { Toggle } from '@/components/forms/Checkbox'
import { Banner } from '@/components/banners/Banner'
import { Button } from '@/components/buttons/Button'
import { Card } from '@/components/cards/Card'
import { Divider } from '@/components/dividers/Divider'
import { Progress } from '@/components/progress/Progress'
import { FormField } from '@/components/forms/FormField'
import { TextInput } from '@/components/forms/TextInput'

export const DynamicRowsSection = ({
  title,
  autoDecideLabel,
  autoDecideName,
  autoDecideValue,
  handleAutoDecideChange,
  rows,
  rowLabel,
  firstFieldLabel,
  firstFieldKey,
  secondFieldLabel,
  secondFieldKey,
  handleRowFieldChange,
  handleAddRow,
  handleRemoveRow,
  disableRemove,
  isDisabled,
}) => {
  return (
    <Card title={title}>
      <FormField className="mb">
        <Toggle
          label={autoDecideLabel}
          name={autoDecideName}
          checked={autoDecideValue}
          onChange={handleAutoDecideChange}
          disabled={isDisabled}
        />
      </FormField>

      {rows.map((row, index) => (
        <Card key={row.id} title={`${rowLabel} ${index + 1}`}>
          <div className="field-row mb">
            <FormField label={firstFieldLabel}>
              <TextInput
                value={row[firstFieldKey]}
                onChange={(event) =>
                  handleRowFieldChange(row.id, firstFieldKey, event.target.value)
                }
                disabled={isDisabled}
              />
            </FormField>
            <FormField label={secondFieldLabel}>
              <TextInput
                value={row[secondFieldKey]}
                onChange={(event) =>
                  handleRowFieldChange(row.id, secondFieldKey, event.target.value)
                }
                disabled={isDisabled}
              />
            </FormField>
          </div>

          <div className="flex gap">
            <Button basic size="small" onClick={handleAddRow}>
              Add another
            </Button>
            <Button
              critical
              size="small"
              onClick={() => handleRemoveRow(row.id)}
              disabled={disableRemove}
            >
              Remove
            </Button>
          </div>
        </Card>
      ))}
    </Card>
  )
}

const CHANNEL_LABEL = {
  chat: 'Chats',
  sms: 'SMS',
  email: 'Emails',
}

export const SetupProgressSection = ({
  setupState,
  onStop,
  getOrganizationLink,
  getCallExplorerLink,
  getOmnichannelExplorerLink,
}) => {
  if (!setupState.isSettingUp && !setupState.isComplete && !setupState.error) {
    return null
  }

  const callCount = setupState.completionData?.call_ids?.length || 0
  const omnichannelChannels = setupState.completionData?.omnichannel?.channels || {}
  const omnichannelEntries = Object.entries(omnichannelChannels).filter(
    ([, summary]) => (summary?.sent || 0) > 0
  )

  return (
    <div className={setupState.isSettingUp ? 'animated-gradient-border' : undefined}>
      <Card title="Setup Progress">
        {setupState.isSettingUp && (
          <>
            <div className="flex-align-center flex-space-between mb">
              <p className="no-margin">{setupState.message}</p>
              <Button destructive size="small" type="button" onClick={onStop}>
                Stop
              </Button>
            </div>
            <Progress
              value={setupState.progress}
              success={setupState.isComplete}
              critical={!!setupState.error}
            />
          </>
        )}

        {setupState.isComplete && (
          <Banner success>
            <p>
              <strong>Setup Complete!</strong>
            </p>
            <p>{setupState.message}</p>

            {setupState.organizationData && (
              <div className="mt">
                <strong>Organization:</strong> {setupState.organizationData?.organization?.name}
                <br />
                <strong>New Users Created:</strong>{' '}
                {setupState.organizationData?.users?.length ?? 0}
                <br />
                <strong>Selected Agents for Calls:</strong>{' '}
                {setupState.organizationData?.selected_users?.length ?? 0}
                <br />
                <strong>Use Case:</strong> {setupState.organizationData?.use_case ?? '—'}
              </div>
            )}

            {setupState.completionData && (
              <>
                <Divider />
                <strong>Next Steps:</strong>

                <div className="flex gap mt">
                  <Link
                    to={getOrganizationLink(setupState.completionData.organization_id)}
                    target="_blank"
                    className="balto-button brand small"
                  >
                    View Organization
                  </Link>

                  {callCount > 0 && (
                    <Link
                      to={getCallExplorerLink(
                        setupState.completionData.call_ids,
                        setupState.completionData.organization_id
                      )}
                      target="_blank"
                      className="balto-button success small"
                    >
                      View Calls ({callCount})
                    </Link>
                  )}

                  {getOmnichannelExplorerLink &&
                    omnichannelEntries.map(([channel, summary]) => (
                      <Link
                        key={channel}
                        to={getOmnichannelExplorerLink(
                          channel,
                          setupState.completionData.organization_id
                        )}
                        target="_blank"
                        className="balto-button success small"
                      >
                        {`View ${CHANNEL_LABEL[channel] || channel} (${summary.sent})`}
                      </Link>
                    ))}
                </div>
              </>
            )}
          </Banner>
        )}

        {setupState.error && (
          <Banner critical>
            <p>
              <strong>Setup Error</strong>
            </p>
            <p>{setupState.error}</p>
          </Banner>
        )}
      </Card>
    </div>
  )
}
